import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { LinkChip } from "@/components/workspace/ActionLinkPicker";
import { ResponsibilityDialog } from "@/components/workspace/team/ResponsibilityDialog";
import { WEEKDAYS } from "@/lib/workspace";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

const cadenceText = (r) => (r.cadence === "weekly" ? `Every ${WEEKDAYS[r.weekday] || "week"}` : `Monthly on day ${r.day_of_month}`);

export function ResponsibilitiesTab({ orgId, people, canEdit, onChanged }) {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => api.get(`/workspace/responsibilities?org_id=${orgId}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (r, active) => {
    try {
      await api.patch(`/workspace/responsibilities/${r.id}`, { active });
      toast.success(active ? "Responsibility resumed" : "Responsibility paused — no new tasks will be created");
      load(); onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!rows) return <Icons.Loader2 className="mx-auto mt-8 animate-spin text-[#FFB000]" />;
  return (
    <div data-testid="responsibilities-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-[#1E2D44]/60">Weekly and monthly duties that create a dated task for each period.</p>
        {canEdit && (
          <Button onClick={() => setCreating(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-responsibility-btn">
            <Icons.Repeat size={16} className="mr-1.5" /> New responsibility
          </Button>
        )}
      </div>
      {rows.length === 0 ? (
        <EmptyState icon="Repeat" title="No recurring responsibilities" description="Set up weekly or monthly duties, such as KPI updates or reports." testId="responsibilities-empty" />
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className={`amden-card flex flex-col gap-3 p-4 sm:flex-row sm:items-center ${r.active ? "" : "opacity-60"}`} data-testid={`responsibility-${r.id}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#4A78A8]/10 text-[#4A78A8]"><Icons.Repeat size={18} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#0B1729]">{r.title}</div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#1E2D44]/55">
                  <LinkChip a={r} />
                  <span>{r.owner_name}</span>
                  <span>{cadenceText(r)}</span>
                  {r.requires_review && <span className="flex items-center gap-1 text-[#B87F1E]"><Icons.ShieldCheck size={12} /> {r.reviewer_name}</span>}
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-[#1E2D44]/55">
                <span data-testid={`resp-next-${r.id}`}>{r.active ? (r.next_due ? `Next due ${fmtDate(r.next_due)}` : "Ended") : "Paused"}</span>
                <span>{r.generated} created</span>
                {canEdit && <Switch checked={!!r.active} onCheckedChange={(v) => toggle(r, v)} data-testid={`resp-active-${r.id}`} />}
              </div>
            </div>
          ))}
        </div>
      )}
      {creating && <ResponsibilityDialog orgId={orgId} people={people} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); onChanged(); }} />}
    </div>
  );
}
